import { ChevronDown, Clock3, type LucideIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { COMPANY } from "../constants/company.constants";
import WebLayout from "../layouts/WebLayout";
import cn from "../lib/cn";
import PageHero from "./PageHero";

/* ----------------------------------------------------------------------------
 * Shared document layout for HRx legal pages (Privacy Policy, Refund Policy)
 * -------------------------------------------------------------------------- */

export type LegalSection = {
  id: string;
  title: string;
  content: ReactNode;
};

type LegalPageProps = {
  badge: { icon: LucideIcon; label: string };
  title: string;
  description: ReactNode;
  dateLabel: string;
  date: string;
  sections: LegalSection[];
};

const NAVBAR_OFFSET = 112; // matches scroll-mt-28 on sections

/* ======================= Content building blocks ======================= */

const bodyText = "text-[15px] leading-7 text-gray-600";

export const Paragraph = ({ children }: { children: ReactNode }) => (
  <p className={bodyText}>{children}</p>
);

export const Strong = ({ children }: { children: ReactNode }) => (
  <span className="font-semibold text-gray-900">{children}</span>
);

export const TextLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => {
  const className =
    "font-medium text-theme-primary underline decoration-theme-primary/30 underline-offset-4 transition-colors hover:decoration-theme-primary";
  // In-app paths go through the router; mailto: and external URLs stay plain links.
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className={className}
    >
      {children}
    </a>
  );
};

export const BulletList = ({
  items,
  dotClassName = "bg-theme-primary",
}: {
  items: ReactNode[];
  dotClassName?: string;
}) => (
  <ul className="space-y-2.5">
    {items.map((item, index) => (
      <li key={index} className={cn("flex gap-3", bodyText)}>
        <span
          className={cn(
            "mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full",
            dotClassName,
          )}
        />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/**
 * Titled blocks with small labelled rows, separated by hairlines, e.g.
 *   Payroll & Financials
 *   Access    Restricted to authorized HR and Finance roles only.
 */
export const DataBlocks = ({
  items,
}: {
  items: { title: string; rows: { label: string; value: ReactNode }[] }[];
}) => (
  <div className="divide-y divide-gray-200 border-y border-gray-200">
    {items.map((item) => (
      <div key={item.title} className="py-4">
        <p className="text-sm font-semibold text-gray-900">{item.title}</p>
        <dl className="mt-2 space-y-1.5">
          {item.rows.map((row) => (
            <div
              key={row.label}
              className="grid gap-0.5 sm:grid-cols-[96px_1fr] sm:gap-4"
            >
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 sm:pt-[3px]">
                {row.label}
              </dt>
              <dd className="text-sm leading-6 text-gray-600">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    ))}
  </div>
);

/** Emphasised note with a brand accent bar. */
export const Note = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) => (
  <div className="border-l-2 border-theme-primary bg-theme-primary/[0.04] py-3 pl-4 pr-4">
    {title && <p className="text-sm font-semibold text-gray-900">{title}</p>}
    <div className={cn(bodyText, title && "mt-0.5")}>{children}</div>
  </div>
);

export const SubHeading = ({ children }: { children: ReactNode }) => (
  <h3 className="pt-2 text-sm font-semibold text-gray-900">{children}</h3>
);

export const Steps = ({ items }: { items: ReactNode[] }) => (
  <ol className="space-y-2.5">
    {items.map((item, index) => (
      <li key={index} className={cn("flex gap-3", bodyText)}>
        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-theme-primary/10 text-[11px] font-semibold text-theme-primary">
          {index + 1}
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

/* ============================== Scroll spy ============================== */

/**
 * Tracks the section under the navbar. After a TOC click the clicked
 * section stays active until the user scrolls by hand — short final
 * sections may never reach the top and would otherwise highlight the
 * wrong item.
 */
const useScrollSpy = (ids: string[]) => {
  const [active, setActive] = useState(ids[0]);
  const lockedRef = useRef(false);

  useEffect(() => {
    const compute = () => {
      if (lockedRef.current) return;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= NAVBAR_OFFSET + 24) {
          current = id;
        }
      }
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4;
      setActive(atBottom ? ids[ids.length - 1] : current);
    };
    // Any manual scrolling hands control back to the spy.
    const unlock = () => {
      lockedRef.current = false;
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("wheel", unlock, { passive: true });
    window.addEventListener("touchmove", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    window.addEventListener("mousedown", unlock); // e.g. scrollbar drag
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("wheel", unlock);
      window.removeEventListener("touchmove", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("mousedown", unlock);
    };
  }, [ids]);

  const goTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    lockedRef.current = true;
    setActive(id);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return { active, goTo };
};

/* ================================= Page ================================= */

const LegalPage = ({
  badge,
  title,
  description,
  dateLabel,
  date,
  sections,
}: LegalPageProps) => {
  const ids = useMemo(() => sections.map((s) => s.id), [sections]);
  const { active, goTo } = useScrollSpy(ids);
  const [tocOpen, setTocOpen] = useState(false);

  const toc = (
    <ol className="border-l border-gray-200">
      {sections.map((section) => {
        const isActive = active === section.id;
        return (
          <li key={section.id}>
            <button
              onClick={() => {
                goTo(section.id);
                setTocOpen(false);
              }}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-[13px] transition-colors",
                isActive
                  ? "border-theme-primary font-medium text-gray-900"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900",
              )}
            >
              {section.title}
            </button>
          </li>
        );
      })}
    </ol>
  );

  return (
    <WebLayout>
      <PageHero badge={badge} title={title} description={description}>
        <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white/70 px-3 py-1.5 text-xs text-gray-500 backdrop-blur">
          <Clock3 className="h-3.5 w-3.5" />
          {dateLabel}: {date}
        </span>
      </PageHero>

      {/* ---------- Body ---------- */}
      <div className="mx-auto grid max-w-5xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[200px_1fr] lg:gap-14 lg:px-8 lg:py-16">
        <aside>
          {/* Mobile */}
          <div className="rounded-xl border border-gray-200 lg:hidden">
            <button
              onClick={() => setTocOpen((open) => !open)}
              aria-expanded={tocOpen}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-900"
            >
              On this page
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-gray-500 transition-transform",
                  tocOpen && "rotate-180",
                )}
              />
            </button>
            {tocOpen && <div className="px-4 pb-4">{toc}</div>}
          </div>

          {/* Desktop */}
          <div className="sticky top-28 hidden lg:block">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              On this page
            </p>
            {toc}
          </div>
        </aside>

        <article className="min-w-0">
          <div className="divide-y divide-gray-200">
            {sections.map((section, index) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-28 py-10 first:pt-0"
              >
                <h2 className="flex items-baseline gap-3 text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
                  <span className="w-6 shrink-0 text-xs font-semibold tabular-nums text-theme-primary">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {section.title}
                </h2>
                <div className="mt-4 space-y-4 sm:pl-9">{section.content}</div>
              </section>
            ))}
          </div>

          <p className="border-t border-gray-200 pt-8 text-sm text-gray-400">
            {COMPANY.product} is a product of{" "}
            <Link
              to={COMPANY.websiteUrl}
              target="_blank"
              className="text-gray-600 underline underline-offset-4 hover:text-theme-primary"
            >
              {COMPANY.name}
            </Link>
          </p>
        </article>
      </div>
    </WebLayout>
  );
};

export default LegalPage;
